/**
 * Los estilos de la app del técnico. Pensados para una mano, al sol, con guantes.
 *
 * ── Tres reglas que no son decoración ──
 *
 * 1. **Nada que se toque mide menos de 48 px de alto.** Es la medida que recomienda Google
 *    para un dedo, y aquí importa más que en la tienda: el que usa esto está de pie, con una
 *    escalera en la otra mano.
 * 2. **Los botones que cambian algo van abajo**, al alcance del pulgar. Arriba solo va lo
 *    que se lee.
 * 3. **Las columnas llevan `minmax(0,1fr)` y `minWidth:0`.** Sin eso una columna no se
 *    encoge por debajo de su contenido y la pantalla se sale — que es exactamente lo que le
 *    pasó a la ficha de producto de la tienda, 563 px fuera de un celular de 375.
 *
 * Los nombres de cliente y las direcciones los escribe quien sea, así que todo lo que sea
 * texto de otro lleva `overflowWrap:'anywhere'`.
 */

export const COLOR = {
  fondo: '#0A0E1A',
  tarjeta: '#111827',
  borde: 'rgba(255,255,255,0.08)',
  texto: '#F1F5F9',
  suave: '#94A3B8',
  acento: '#00E5FF',
  verde: '#10B981',
  ambar: '#F59E0B',
  rojo: '#EF4444',
  morado: '#6366F1',
};

/** Cada prioridad con su color. El mismo en la lista y en la ficha. */
export const PRIORIDAD = {
  urgente: { color: COLOR.rojo,  label: 'Urgente' },
  alta:    { color: COLOR.ambar, label: 'Alta' },
  normal:  { color: COLOR.acento, label: 'Normal' },
  baja:    { color: COLOR.suave, label: 'Baja' },
};

/** Y cada estado. «En proceso» existe desde que la base lo acepta (ver asegurarColumnas). */
export const ESTADO = {
  abierto:    { color: COLOR.ambar,  label: 'Por hacer',  icono: '⭕' },
  en_proceso: { color: COLOR.morado, label: 'En proceso', icono: '🔧' },
  resuelto:   { color: COLOR.verde,  label: 'Terminado',  icono: '✅' },
  cerrado:    { color: COLOR.suave,  label: 'Cerrado',    icono: '🔒' },
  cancelado:  { color: COLOR.suave,  label: 'Cancelado',  icono: '🚫' },
};

export const TIPO = {
  falla:          '🔧 Falla',
  instalacion:    '🆕 Instalación',
  mantenimiento:  '🛠️ Mantenimiento',
  retiro:         '📦 Retiro',
  visita_tecnica: '🚗 Visita',
  otro:           '📌 Otro',
};

export const s = {
  pantalla: {
    minHeight: '100vh', background: COLOR.fondo, color: COLOR.texto,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    // Sitio para la barra de abajo, y para el borde curvo de los celulares nuevos.
    paddingBottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
  },
  cabecera: {
    position: 'sticky', top: 0, zIndex: 20,
    background: 'rgba(10,14,26,0.95)', backdropFilter: 'blur(8px)',
    borderBottom: `1px solid ${COLOR.borde}`,
    padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px 12px',
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  cuerpo: { padding: '16px', display: 'grid', gap: '12px' },

  tarjeta: {
    background: COLOR.tarjeta, border: `1px solid ${COLOR.borde}`,
    borderRadius: '14px', padding: '14px', minWidth: 0,
  },
  titulo: { fontSize: '16px', fontWeight: 700, lineHeight: 1.35, overflowWrap: 'anywhere' },
  sub: { fontSize: '13px', color: COLOR.suave, overflowWrap: 'anywhere' },

  chip: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '999px',
    whiteSpace: 'nowrap',
  },

  boton: {
    minHeight: '48px', width: '100%', borderRadius: '12px', border: 'none',
    fontSize: '15px', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    padding: '0 16px',
  },
  botonSuave: {
    minHeight: '48px', width: '100%', borderRadius: '12px',
    border: `1px solid ${COLOR.borde}`, background: 'transparent', color: COLOR.texto,
    fontSize: '15px', fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    padding: '0 16px',
  },

  campo: {
    width: '100%', boxSizing: 'border-box', minHeight: '48px',
    background: 'rgba(255,255,255,0.04)', border: `1px solid ${COLOR.borde}`,
    borderRadius: '12px', color: COLOR.texto, fontSize: '15px', padding: '12px',
    fontFamily: 'inherit',
  },

  barra: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30,
    background: 'rgba(10,14,26,0.97)', backdropFilter: 'blur(8px)',
    borderTop: `1px solid ${COLOR.borde}`,
    display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
  barraBoton: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '10px 4px 12px', display: 'grid', gap: '3px', justifyItems: 'center',
    fontSize: '11px', fontWeight: 600, minHeight: '60px', minWidth: 0,
  },

  aviso: {
    borderRadius: '12px', padding: '12px 14px', fontSize: '13px', lineHeight: 1.5,
    display: 'flex', gap: '10px', alignItems: 'flex-start',
  },
  vacio: { textAlign: 'center', padding: '48px 24px', color: COLOR.suave, lineHeight: 1.6 },
};
